import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { ToastService } from '@pflegix/ui';
import { isError } from '@pflegix/util';

/**
 * List of error messages that are blacklisted and only logged to the console,
 * but not shown as a toast.
 */
const BLACKLISTED_ERRORS = [
  'Invalid time value',
  'is not a function',
  'undefined is not an object',
  'Cannot read properties of null',
  'Cannot read properties of undefined',
  'NullInjectorError',
  'NG0100: ExpressionChangedAfterItHasBeenCheckedError',
  'Interleaved load operation detected',
] as const;

/**
 * Global error handler class that implements the ErrorHandler interface.
 * This class is responsible for handling and displaying error messages globally.
 */
@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);

  /**
   * Retrieves the ToastService instance from the injector.
   * @returns The ToastService instance.
   */
  private get toastService(): ToastService {
    return this.injector.get(ToastService);
  }

  /**
   * Determines whether the error should be skipped for notification.
   *
   * @param error - The error to check.
   * @returns True if the error should be skipped, false otherwise.
   */
  private static shouldSkipErrorNotification(error: unknown): boolean {
    if (!isError(error)) {
      return false;
    }

    return BLACKLISTED_ERRORS.some((blacklistedMessage) =>
      error.message.toLowerCase().includes(blacklistedMessage.toLowerCase())
    );
  }

  private static logErrorToConsole(error: Error): void {
    // eslint-disable-next-line no-console
    console.error(`${GlobalErrorHandler.name}: Skipped error notification for error:`, error);
  }

  /**
   * Handles the given error by adding an error toast using the ToastService.
   * @param error - The error to be handled.
   */
  public handleError(error: Error): void {
    GlobalErrorHandler.logErrorToConsole(error);

    if (GlobalErrorHandler.shouldSkipErrorNotification(error)) {
      return;
    }

    this.handleChunkFailedError(error);

    this.toastService.addErrorToast(error);
  }

  public handleChunkFailedError(error: Error): void {
    const chunkFailedMessage = /Loading chunk \d+ failed/;

    if (chunkFailedMessage.test(error.message)) {
      this.toastService.addErrorToast('Core Version veraltet. Bitte Seite neu laden!');
    }
  }
}
